import React, { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import pako from "pako";

import ErrorPage from "../components/error-page";
import SecurityBadge from "../components/secrity-badge";
import "./operate.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const ERRORS = {
  corrupt: "The link you followed may be broken or removed",
  unknown: "Failed to load. Operator might not exist.",
  network: "Check your network connection and try again",
};

const getOperatorUrl = () => {
  const { origin, pathname, search } = window.location;
  const params = new URLSearchParams(search);
  return `${origin}${pathname}?${params.toString()}`;
};

const decodeSession = (encodedPayment) => {
  if (!encodedPayment) return null;

  try {
    // Fix '+' turned into spaces
    let session = encodedPayment.replace(/ /g, "+");

    // Decode percent-encoding
    session = decodeURIComponent(session);

    // Base64 decode → Uint8Array
    const binaryStr = atob(session);
    const binaryData = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
      binaryData[i] = binaryStr.charCodeAt(i);
    }

    // Gzip decompress
    const decompressed = pako.ungzip(binaryData, { to: "string" });

    // Parse query string
    const parsed = Object.fromEntries(new URLSearchParams(decompressed));

    console.log(parsed);
    return parsed;
  } catch {
    return null;
  }
};

const getInitials = (name = "?") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();

const Operate = () => {
  const [userName, setUserName] = useState(null);
  const [name, setName] = useState("?");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const operatorUrl = getOperatorUrl();
  useEffect(() => {
    const init = async () => {
      const sessionParam = new URLSearchParams(window.location.search).get(
        "session"
      );
      if (!sessionParam) {
        setError(ERRORS.corrupt);
        setLoading(false);
        return;
      }

      const params = decodeSession(sessionParam);
      if (!params?.userName || Object.keys(params).length > 1) {
        setError(ERRORS.corrupt);
        setLoading(false);
        return;
      }

      setUserName(params.userName);

      try {
        const queryParams = new URLSearchParams({
          userName: params.userName,
          isOperator: true,
        });
        const res = await fetch(`${API_BASE}/getFullName?${queryParams}`, {
          headers: { "api-version": "v1" },
          "Content-Type": "application/json",
        });

        if (!res.ok) {
          setError(ERRORS.network);
          return;
        }

        const data = await res.json();
        setName(data.fullName || "Unknown");
      } catch {
        setError(ERRORS.unknown);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [navigate]);

  if (loading) {
    return (
      <div className="op-box">
        <div className="op-loader"></div>
      </div>
    );
  }

  if (error) return <ErrorPage error={error} />;

  const initials = getInitials(name);

  return (
    <div className="op-box">
      <Helmet>
        <title>CashOver Pay | Secure Deposit & Withdrawal Services</title>
        <meta
          name="description"
          content="Deposit or withdraw money safely and conveniently with CashOver through our trusted agents and ATMs."
        />
        <meta
          property="og:title"
          content="Deposit & Withdraw Cash Easily with CashOver Operators"
        />
        <meta
          property="og:description"
          content="Use CashOver to deposit or withdraw funds securely via our network of verified agents and ATMs."
        />
        <meta
          property="og:image"
          content={`${process.env.REACT_APP_SITE_URL}/operate.webp`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={operatorUrl} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="operate-container">
        {/* Left side */}
        <div className="op-left-side">
          <Link to="/" className="op-link-logo">
            <div className="op-logo-row">
              <img alt="CashOver Logo" src="/icon.webp" className="op-logo" />
              <div className="op-logo-column">
                <span className="op-logo-text3">CASHOVER</span>
                <span className="op-logo-text4">by overlab.io</span>
              </div>
            </div>
          </Link>

          <div
            className="op-row"
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 32,
            }}
          >
            <div style={{ flex: 1 }}>
              <h2 className="op-name">{name}</h2>
              <span className="op-info">
                Head to {name} and scan this QR code to deposit or withdraw cash
                instantly.
              </span>
            </div>
            <div className="op-avatar">{initials}</div>
          </div>

          <div className="op-scan-box">
            <div className="op-scan-text">Scan to Withdraw or Deposit</div>
            <div className="op-qr-code-box">
              <QRCodeCanvas
                value={operatorUrl}
                size={250}
                bgColor="#161616"
                fgColor="white"
                level="H"
                imageSettings={{
                  excavate: true,
                  src: "/icon.webp",
                  height: 60,
                  width: 60,
                }}
              />
            </div>
          </div>

          <SecurityBadge />
        </div>

        {/* Right side */}
        <div className="op-right-side">
          <div className="op-right-side-column">
            <h2 className="op-right-side-title">CASHOVER NETWORK</h2>
            <h2 className="op-right-side-subtitle">Change the way you money</h2>
            <Link to="/" className="op-learn-more-text">
              Learn more
            </Link>
          </div>
          <img src="/operate.webp" style={{ borderRadius: 24 }} />
        </div>
      </div>
    </div>
  );
};

export default Operate;
